// Pages/ComplaintBoardPage.js
import React, { useEffect, useState } from 'react';
import './ComplaintBoardPage.css';
import { getComplaintBoard } from '../../services/apiService';
import { Helmet } from "react-helmet-async";

/**
 * Audit fixes applied in this pass:
 * 1. Tables:
 *    - Added a <caption> (visually hidden, but announced to screen readers)
 *      to every table describing what it contains.
 *    - Added scope="col" to every column header.
 *    - Added scope="row" to the header-like value in each data row
 *      (Received From / Month / Year) so screen readers announce the
 *      row's identity as you move across cells.
 *    - Each scrollable table wrapper is now a keyboard-reachable region
 *      (tabIndex="0", role="region", aria-label + visible focus ring),
 *      so keyboard-only users can Tab to it and use arrow keys to scroll
 *      horizontally when the table overflows on narrow screens. Previously
 *      the wrapper was a plain <div> with no way to reach it by keyboard.
 * 2. Contrast: moved all color/contrast fixes into ComplaintBoardPage.css
 *    (header text/background, zebra striping, focus rings, and the
 *    "View History" button all now meet WCAG AA 4.5:1 for text).
 * 3. Page title: the browser tab title was never set (fell back to the
 *    app's default/generic title), which fails "each page has a unique,
 *    descriptive title" audits. Now set explicitly via <Helmet>.
 * 4. Complaint summary table heading: the first table previously only had
 *    a visually-hidden <h2> ("Complaint Summary"), so sighted users saw no
 *    heading above it at all — inconsistent with the other two tables on
 *    this page, which both have visible headings. Fixed by making the
 *    heading visible and using a "Data for the Month Ending <Month Year>"
 *    format, consistent with SEBI complaint board conventions.
 */

// ---- Fallback data (used if the API is unavailable) ----
const defaultComplaintData = [
  { srNo: 1, receivedFrom: 'Directly from Investors', pendingAtEndOfLastMonth: 0, received: 0, receivedStar: 0, totalPending: 0, pendingComplaintOver3Months: 0, avgResolutionTime: 0 },
  { srNo: 2, receivedFrom: 'SEBI (SCORES)', pendingAtEndOfLastMonth: 0, received: 5, receivedStar: 5, totalPending: 0, pendingComplaintOver3Months: 0, avgResolutionTime: 8 },
  { srNo: 3, receivedFrom: 'Other Sources (If any)', pendingAtEndOfLastMonth: 0, received: 0, receivedStar: 0, totalPending: 0, pendingComplaintOver3Months: 0, avgResolutionTime: 0 },
  { srNo: '', receivedFrom: 'Grand Total', pendingAtEndOfLastMonth: 0, received: 0, receivedStar: 0, totalPending: 0, pendingComplaintOver3Months: 0, avgResolutionTime: '8' },
];

const defaultTrendData = [
  { srNo: 1, month: 'May-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 2, month: 'June-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 3, month: 'July-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 4, month: 'Aug-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 5, month: 'Sep-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 6, month: 'Oct-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 7, month: 'Nov-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 8, month: 'Dec-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 9, month: 'Jan-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 10, month: 'Feb-2025', carriedForwardFromPreviousMonth: 0, received: 1, receivedStar: 1, pending: 0 },
  { srNo: 11, month: 'March-2025', carriedForwardFromPreviousMonth: 0, received: 4, receivedStar: 0, pending: 4 },
  { srNo: 12, month: 'April-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 13, month: 'May-2025', carriedForwardFromPreviousMonth: 4, received: 0, receivedStar: 4, pending: 0 },
  { srNo: '', month: 'Grand Total', carriedForwardFromPreviousMonth: 0, received: 5, receivedStar: 5, pending: 0 },
];

const defaultAnnualData = [
  { srNo: 1, year: '2024-2025', carriedForwardFromPreviousMonth: 0, received: 0, receivedStar: 0, pending: 0 },
  { srNo: 2, year: '2025-2026', carriedForwardFromPreviousMonth: 0, received: 5, receivedStar: 5, pending: 0 },
  { srNo: '', year: 'Grand Total', carriedForwardFromPreviousMonth: 0, received: 5, receivedStar: 5, pending: 0 },
];

// Parse a month label like "May-2025", "March-2025" into { year, month }.
// Returns null for non-month rows (e.g. "Grand Total").
const MONTH_MAP = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};
const parseMonth = (label) => {
  if (!label) return null;
  const m = String(label).trim().toUpperCase().match(/^([A-Z]+)[-\s]*(\d{2,4})$/);
  if (!m) return null;
  const month = MONTH_MAP[m[1].slice(0, 3)];
  if (!month) return null;
  let year = parseInt(m[2], 10);
  if (year < 100) year += 2000;
  return { year, month };
};

const PAGE_TITLE = 'Complaint Board | Option Bricks';

function ComplaintBoardPage() {
  const [data, setData] = useState(defaultComplaintData);
  const [trendData, setTrendData] = useState(defaultTrendData);
  const [trendAnnualData, setTrendAnnualData] = useState(defaultAnnualData);
  const [showAllMonths, setShowAllMonths] = useState(false);

  // Fix: set a unique, descriptive document title for this page (fallback,
  // in case Helmet hasn't hydrated yet on first paint).
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getComplaintBoard()
      .then((res) => {
        if (!active || !res) return;
        if (Array.isArray(res.complaintBoardData) && res.complaintBoardData.length)
          setData(res.complaintBoardData);
        if (Array.isArray(res.monthlyTrendData) && res.monthlyTrendData.length)
          setTrendData(res.monthlyTrendData);
        if (Array.isArray(res.annualTrendData) && res.annualTrendData.length)
          setTrendAnnualData(res.annualTrendData);
      })
      .catch(() => {
        // Keep fallback data if the API is unavailable.
      });
    return () => {
      active = false;
    };
  }, []);

  // Monthly table: show only the current calendar year up to the current month
  // by default; the "View History" toggle shows all months.
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Human-readable "Month Year" label for the complaint summary table heading,
  // e.g. "June 2026".
  const dataAsOfLabel = now.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const currentYearTrend = trendData.filter((r) => {
    const p = parseMonth(r.month);
    return p && p.year === currentYear && p.month <= currentMonth;
  });
  const displayedTrend =
    showAllMonths || currentYearTrend.length === 0
      ? trendData
      : currentYearTrend.map((r, i) => ({ ...r, srNo: i + 1 }));

  return (
    <div className="complaint-board-page">

      <Helmet>
        <title>{PAGE_TITLE}</title>
      </Helmet>

      <h1>Complaint Board</h1>

      {/* First Table: Complaint Summary — now has a VISIBLE heading, matching
          the pattern of the other two tables on this page. */}
      <h2 id="complaint-summary-heading">
        Data for the Month Ending {dataAsOfLabel}
      </h2>
      <div
        className="table-wrapper"
        role="region"
        aria-labelledby="complaint-summary-heading"
        tabIndex="0"
      >
        <table className="complaint-table">
          <caption className="visually-hidden">
            Summary of complaints received directly from investors, via SEBI SCORES, and other
            sources, for the month ending {dataAsOfLabel}, showing pending, received, resolved,
            and average resolution time.
          </caption>
          <thead>
            <tr>
              <th scope="col">Sr No</th>
              <th scope="col">Received From</th>
              <th scope="col">Pending at end of last month</th>
              <th scope="col">Received</th>
              <th scope="col">Resolved*</th>
              <th scope="col">Total Pending #</th>
              <th scope="col">Pending Complaint{'>'}3 month</th>
              <th scope="col">Average Resolution Time (in days)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx}>
                <td>{item.srNo}</td>
                <th scope="row">{item.receivedFrom}</th>
                <td>{item.pendingAtEndOfLastMonth}</td>
                <td>{item.received}</td>
                <td>{item.receivedStar}</td>
                <td>{item.totalPending}</td>
                <td>{item.pendingComplaintOver3Months}</td>
                <td>{item.avgResolutionTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Second Table: Trend of Monthly Disposal of Complaints */}
      <div className="section-heading-row">
        <h2 id="monthly-trend-heading">Trend of Monthly Disposal of Complaints</h2>
        <button
          type="button"
          className="history-toggle-btn"
          onClick={() => setShowAllMonths((v) => !v)}
          aria-pressed={showAllMonths}
        >
          {showAllMonths ? 'Show Current Year' : 'View History'}
        </button>
      </div>
      {!showAllMonths && (
        <p className="table-note">
          Showing {currentYear} (up to current month). Click &ldquo;View History&rdquo; for all months.
        </p>
      )}
      <div
        className="table-wrapper"
        role="region"
        aria-labelledby="monthly-trend-heading"
        tabIndex="0"
      >
        <table className="trend-table">
          <caption className="visually-hidden">
            Month-by-month count of complaints carried forward, received, resolved, and pending.
          </caption>
          <thead>
            <tr>
              <th scope="col">Sr No</th>
              <th scope="col">Month</th>
              <th scope="col">Carried Forward from Previous Month</th>
              <th scope="col">Received</th>
              <th scope="col">Resolved*</th>
              <th scope="col">Pending #</th>
            </tr>
          </thead>
          <tbody>
            {displayedTrend.map((item, idx) => (
              <tr key={idx}>
                <td>{item.srNo}</td>
                <th scope="row">{item.month}</th>
                <td>{item.carriedForwardFromPreviousMonth}</td>
                <td>{item.received}</td>
                <td>{item.receivedStar}</td>
                <td>{item.pending}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Third Table: Trend of Annual Disposal of Complaints */}
      <h2 id="annual-trend-heading">Trend of Annual Disposal of Complaints</h2>
      <div
        className="table-wrapper"
        role="region"
        aria-labelledby="annual-trend-heading"
        tabIndex="0"
      >
        <table className="trend-table">
          <caption className="visually-hidden">
            Year-by-year count of complaints carried forward, received, resolved, and pending.
          </caption>
          <thead>
            <tr>
              <th scope="col">Sr No</th>
              <th scope="col">Year</th>
              <th scope="col">Carried Forward from Previous Month</th>
              <th scope="col">Received</th>
              <th scope="col">Resolved</th>
              <th scope="col">Pending #</th>
            </tr>
          </thead>
          <tbody>
            {trendAnnualData.map((item, idx) => (
              <tr key={idx}>
                <td>{item.srNo}</td>
                <th scope="row">{item.year}</th>
                <td>{item.carriedForwardFromPreviousMonth}</td>
                <td>{item.received}</td>
                <td>{item.receivedStar}</td>
                <td>{item.pending}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ComplaintBoardPage;